/**
 * APP Plus measure sheet — the VERBATIM MIPS Gap Management sheet structure
 * (Summary.tsx L5240-5488), owner-locked 2026-07-14 (UI_ALIGNMENT §8):
 * navy ID block · CMS Avg donut · pills row · Measure Spec button ·
 * Calculated Metrics card · six-column counts table WITH INLINE INPUTS
 * (this is THE entry surface for clinical measures) · benchmark bar (muted) ·
 * Notes editor · evidence section (same feature flag as MIPS).
 */
import { useEffect, useState } from 'react';
import { ExternalLink, Info as InfoIcon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import RichTextEditor from '@/components/gap-management/shared/MeasureNotesEditor';
import Dropzone from '@/components/gap-management/shared/MeasureEvidenceFileUploader';
import DecileBenchmarkBar, { type DecileRange } from '@/components/gap-management/shared/BenchmarkChart';
import { isFeatureEnabled } from '@/config/features';
import userStorage from '@/lib/userStorage';
import { MipsDataService } from '@/lib/mips-data-service';
import type { AppMeasure, AppBenchmarkCell } from '@/lib/app-plus-data-service';
import type { MeasureScoreResult } from '@/lib/app-plus-scoring';
import type { StoredSubmission } from '../lib/useAppPlusScoring';

const NOTES_KEY = 'app:measureNotes';

function specUrlFor(measureId: string, collectionType: string, year: number): string | null {
  try {
    const cms = MipsDataService.getInstance().getActiveMeasure(measureId) as
      { measureSpecification?: Record<string, string | undefined> } | undefined;
    const spec = cms?.measureSpecification;
    if (spec) {
      const url = collectionType === 'eCQM'
        ? spec.electronicHealthRecord
        : spec.registry;
      if (url) return url;
    }
  } catch { /* fall through to the QPP search link */ }
  return `https://qpp.cms.gov/mips/explore-measures?tab=qualityMeasures&py=${year}&search=${measureId}`;
}

interface AppMeasureSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  measure: AppMeasure | null;
  /** Merged (persisted + draft) row for this measure. */
  row: StoredSubmission | null;
  result: MeasureScoreResult | null;
  cell: AppBenchmarkCell | null;
  deciles: DecileRange[];
  year: number;
  /** True for the five clinical measures — counts are editable here. */
  isClinical: boolean;
  locked: boolean;
  onPatch: (measureId: string, patch: Partial<StoredSubmission>) => void;
}

export function AppMeasureSheet({
  open, onOpenChange, measure, row, result, cell, deciles, year, isClinical, locked, onPatch,
}: AppMeasureSheetProps) {
  const { toast } = useToast();
  const [notes, setNotes] = useState<Record<string, string>>(() => {
    try { return JSON.parse(userStorage.getItem(NOTES_KEY) || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    try { userStorage.setItem(NOTES_KEY, JSON.stringify(notes)); } catch { /* quota */ }
  }, [notes]);

  if (!measure) {
    return <Sheet open={open} onOpenChange={onOpenChange}><SheetContent /></Sheet>;
  }

  const cmsAvgRaw = (cell?.row as unknown as Record<string, string> | null)?.cmsAverage;
  const cmsAvg = cmsAvgRaw && cmsAvgRaw !== '--' ? parseFloat(cmsAvgRaw) : null;
  const den = (row?.eligible_population ?? 0) - (row?.denominator_exclusions ?? 0);
  const specUrl = specUrlFor(measure.measureId, row?.collection_type ?? 'MIPS CQM', year);

  const countField = (key: keyof StoredSubmission, value: number | null | undefined, hoverBg: string) => (
    isClinical && !locked ? (
      <div className={`h-10 px-3 py-2 text-sm font-medium text-slate-700 text-center bg-white ${hoverBg} cursor-pointer transition-all duration-200 flex items-center justify-center group-hover:shadow-inner`}>
        <Input
          type="number"
          min={0}
          value={value ?? ''}
          onChange={(e) => onPatch(measure.measureId, {
            [key]: e.target.value === '' ? null : Number(e.target.value),
            reported: true,
          } as Partial<StoredSubmission>)}
          onFocus={(e) => e.target.select()}
          className="border-0 p-0 h-auto text-center bg-transparent focus:ring-0 focus:border-0 shadow-none w-full font-medium text-slate-700"
        />
      </div>
    ) : (
      <div className="h-10 px-3 py-2 text-sm font-medium text-slate-600 text-center bg-white flex items-center justify-center">
        {value ?? '—'}
      </div>
    )
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-background sm:max-w-[48rem] w-[95vw] overflow-y-auto">
        <div className="relative px-6 pt-2 pb-6">
          <SheetHeader>
            <div className="space-y-3">
              {/* Top row: ID block + title + CMS circle (verbatim MIPS structure) */}
              <div className="flex items-start justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-800 text-white px-4 py-2 rounded-lg font-bold text-lg flex-shrink-0">
                    {measure.measureId}
                  </div>
                  <div>
                    <SheetTitle className="text-xl font-bold text-slate-900 leading-tight">{measure.name}</SheetTitle>
                    <div className="text-sm text-slate-500 mt-1">
                      Collection: {row?.collection_type ?? measure.collectionTypes[0]} &bull; Measure type: {measure.isOutcome ? 'outcome' : 'process'}
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 pt-1">
                  {cmsAvg != null && (
                    <div
                      className="relative w-16 h-16 rounded-full flex items-center justify-center"
                      style={{ background: `conic-gradient(#1e40af ${cmsAvg * 3.6}deg, #e5e7eb ${cmsAvg * 3.6}deg)` }}
                    >
                      <div className="absolute w-[58px] h-[58px] bg-background rounded-full flex flex-col items-center justify-center">
                        <span className="text-lg font-bold text-blue-800">{cmsAvg.toFixed(0)}%</span>
                        <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">CMS AV</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom row: pills + spec button */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70">Inverse: {measure.isInverse ? 'Yes' : 'No'}</Badge>
                  <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70">Outcome: {measure.isOutcome ? 'Yes' : 'No'}</Badge>
                  <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70">Case min: {measure.caseMinimum}</Badge>
                  {cell?.kind === 'flat' && (
                    <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80">Flat benchmark</Badge>
                  )}
                  {cell?.kind === 'none' && (
                    <Badge variant="secondary" className="rounded-full bg-amber-50 text-amber-700 border-amber-200">Awaiting benchmark</Badge>
                  )}
                </div>
                <Button
                  size="sm"
                  className="bg-blue-800 hover:bg-blue-900 text-white font-medium shadow-sm"
                  onClick={() => window.open(specUrl!, '_blank', 'noopener,noreferrer')}
                  disabled={!specUrl}
                >
                  <ExternalLink className="h-4 w-4 mr-1.5" />
                  Measure Spec
                </Button>
              </div>
            </div>
          </SheetHeader>

          <div className="border-t border-slate-200 my-4"></div>

          <div className="space-y-4">
            {/* Calculated Metrics (verbatim MIPS card) */}
            <div>
              <h3 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wider">Calculated Metrics</h3>
              <div className="bg-white border border-slate-200/60 rounded-xl shadow-sm p-3">
                <div className="grid grid-cols-3 gap-4 items-center">
                  <div className="col-span-2">
                    <div className="text-sm text-slate-600 font-medium">Performance Rate</div>
                    <div className="text-3xl font-bold text-blue-800">
                      {result?.rate != null ? `${result.rate.toFixed(2)}%` : '—'}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 bg-slate-200 rounded-full h-2">
                        <div className="bg-blue-800 h-2 rounded-full" style={{ width: `${Math.min(100, result?.rate ?? 0)}%` }}></div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{measure.isInverse ? 'Lower is better' : 'Higher is better'}</div>
                  </div>
                  <div className="col-span-1 space-y-2">
                    <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-2">
                      <div className="text-xs text-slate-500 font-medium">Complete Rate</div>
                      <div className="text-base font-bold text-slate-800">
                        {result?.dataCompletenessPct != null ? `${result.dataCompletenessPct.toFixed(2)}%` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-2">
                        <div className="text-xs text-slate-500 font-medium">Score (est)</div>
                        <div className="text-base font-bold text-purple-600">
                          {result ? `${result.points.toFixed(1)} / 10 pts` : 'N/A'}
                        </div>
                      </div>
                      <div className="text-xs text-slate-500 text-right mt-1">Based on benchmark</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Six-column counts table — THE entry surface (verbatim MIPS grid) */}
            <div className="bg-white border border-slate-200/60 rounded-xl shadow-sm overflow-hidden">
              <div className="grid grid-cols-6 bg-gradient-to-r from-slate-50 to-slate-50/80">
                {['Eligible Pop.', 'Exclusions', 'Denominator', 'Met', 'Exception', 'Not Met'].map(h => (
                  <div key={h} className="text-xs font-semibold text-slate-600 text-center py-2 px-3 border-r border-slate-200/50 last:border-r-0 tracking-wide uppercase">{h}</div>
                ))}
              </div>
              <div className="grid grid-cols-6 bg-white border-t border-slate-200/30">
                <div className="group border-r border-slate-200/30">{countField('eligible_population', row?.eligible_population, 'hover:bg-blue-50/50')}</div>
                <div className="group border-r border-slate-200/30">{countField('denominator_exclusions', row?.denominator_exclusions, 'hover:bg-blue-50/50')}</div>
                <div className="border-r border-slate-200/30">
                  <div className="h-10 px-3 py-2 text-sm font-semibold text-slate-500 text-center bg-slate-50/50 flex items-center justify-center relative">
                    <div className="absolute top-1 right-1 w-1.5 h-1.5 bg-slate-300 rounded-full" title="Calculated field"></div>
                    {row?.eligible_population != null ? Math.max(0, den).toLocaleString() : '—'}
                  </div>
                </div>
                <div className="group border-r border-slate-200/30">{countField('performance_met', row?.performance_met, 'hover:bg-emerald-50/50')}</div>
                <div className="group border-r border-slate-200/30">{countField('denominator_exceptions', row?.denominator_exceptions, 'hover:bg-amber-50/50')}</div>
                <div className="group">{countField('performance_not_met', row?.performance_not_met, 'hover:bg-red-50/50')}</div>
              </div>
            </div>
            {!isClinical && (
              <p className="text-xs text-muted-foreground -mt-2">
                {measure.measureId === '321'
                  ? 'CAHPS is entered per Summary Survey Measure in Configuration → CAHPS Entry.'
                  : 'Administrative claims values are entered in Configuration → Claims Measures.'}
              </p>
            )}

            <Separator />

            {/* Benchmark */}
            <div>
              <h3 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wider">Benchmark</h3>
              <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm p-4">
                {deciles.length > 0 ? (
                  <DecileBenchmarkBar
                    value={result?.rate ?? 0}
                    deciles={deciles}
                    inverse={measure.isInverse}
                    variant="muted"
                    cmsAvg={cmsAvg ?? undefined}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No benchmark exists for this collection type yet — CMS publishes performance-period
                    benchmarks in summer 2027. Reporting this cell scores as AWAITING (excluded; the
                    regulatory floor protects the composite).
                  </p>
                )}
                {cell?.kind === 'flat' && (
                  <p className="mt-2 text-xs text-muted-foreground">Flat percentage bands (uniform 10-point ladder).</p>
                )}
              </div>
            </div>

            <Separator />

            {/* Notes */}
            <div>
              <h3 className="text-sm font-semibold mb-4">NOTES</h3>
              <RichTextEditor
                value={notes[measure.measureId] || ''}
                onChange={(html: string) => setNotes(prev => ({ ...prev, [measure.measureId]: html }))}
                placeholder="Add comments and notes..."
              />
            </div>

            {/* Evidence (same feature flag behavior as MIPS) */}
            <div className="space-y-3">
              <div className="text-sm font-medium">Upload evidence/screenshots</div>
              {isFeatureEnabled('EVIDENCE_FILES_ENABLED') ? (
                <Dropzone
                  onFiles={() => toast({ title: 'Evidence storage', description: 'Evidence persistence for APP Plus lands with the ACO-upload wiring phase.' })}
                  onError={(msg: string) => toast({ title: 'Upload error', description: msg })}
                  accept={["image/*", ".pdf", ".csv", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"]}
                />
              ) : (
                <Alert>
                  <InfoIcon className="h-4 w-4" />
                  <AlertDescription>Evidence file uploads are temporarily disabled.</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
